import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ExternalLink, X } from "lucide-react";
import { Button } from "@/components/ui";
import { SkeletonGroup, SkeletonText, Skeleton } from "@/components/Skeleton";
import { fetchLegalDocument, LEGAL_PAGE_URLS } from "../api/legal";
import "../legal.css";

const TITLE_KEYS = { terms: "terms_conditions", privacy: "privacy_policy" };
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
// Matches --animate-dialog-out / --animate-fade-out in index.css.
const CLOSE_ANIMATION_MS = 180;

// Gregorian dates with Latin digits in Arabic too, like the rest of the app's dates.
const formatDate = (value, lang) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "ar" ? "ar-SA-u-ca-gregory-nu-latn" : lang;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
};

/**
 * The Terms & Conditions or Privacy Policy in a popup, so a half-filled form stays on screen.
 * The text comes from the platform (managed in Tarh Portal), loaded on first open. `onAgree`, when given,
 * adds an "I agree" button that the page uses to tick its consent checkbox.
 */
export default function LegalDocumentModal({ type, open, onClose, onAgree }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [closing, setClosing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", document: null });

  // Every way of closing plays the close animation first, mirroring the open one (instant with reduced motion).
  const requestClose = () => {
    if (closing) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      onClose();
      return;
    }
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setClosing(false);
      onClose();
    }, CLOSE_ANIMATION_MS);
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setState({ status: "loading", document: null });
    fetchLegalDocument(type, lang)
      .then((document) => {
        if (active) setState({ status: document?.content ? "ready" : "empty", document });
      })
      .catch(() => {
        if (active) setState({ status: "error", document: null });
      });
    return () => {
      active = false;
    };
  }, [open, type, lang, attempt]);

  // While open: the page behind doesn't scroll, Esc closes, Tab stays inside, and focus returns afterwards.
  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const { status, document: legalDocument } = state;
  const unavailable = status === "error" || status === "empty";

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-stretch justify-center bg-black/45 backdrop-blur-[2px] motion-reduce:animate-none sm:items-center sm:p-4 ${
        closing ? "pointer-events-none animate-fade-out" : "animate-fade-in"
      }`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`flex h-full w-full flex-col bg-white shadow-2xl outline-none motion-reduce:animate-none dark:bg-slate-900 sm:h-auto sm:max-h-[85vh] sm:max-w-3xl sm:rounded-2xl ${
          closing ? "animate-dialog-out" : "animate-modal-in"
        }`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-slate-700 sm:px-6">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-gray-900 dark:text-white">
              {t(TITLE_KEYS[type])}
            </h2>
            {status === "ready" && legalDocument?.updated_at && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                {legalDocument.version ? `${t("legal_version", { version: legalDocument.version })} · ` : ""}
                {t("legal_last_updated", { date: formatDate(legalDocument.updated_at, lang) })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label={t("close")}
            className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B8354]/40 motion-reduce:transition-none dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {status === "loading" && (
            <SkeletonGroup label={t("loading")} className="space-y-5">
              <Skeleton className="h-5 w-2/5" />
              <SkeletonText lines={4} />
              <Skeleton className="h-5 w-1/3" />
              <SkeletonText lines={3} />
            </SkeletonGroup>
          )}

          {status === "ready" && (
            <div
              className="legal-content"
              lang={legalDocument.locale}
              dir={legalDocument.locale === "ar" ? "rtl" : "ltr"}
              // Sanitized on the server before it is stored and again before it is served.
              dangerouslySetInnerHTML={{ __html: legalDocument.content }}
            />
          )}

          {unavailable && (
            <div className="flex flex-col items-center gap-4 py-12 text-center">
              <p role="alert" className="max-w-sm text-sm text-gray-600 dark:text-slate-300">
                {t(status === "error" ? "legal_load_failed" : "legal_not_available")}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {status === "error" && (
                  <Button variant="outline" size="sm" onClick={() => setAttempt((count) => count + 1)}>
                    {t("retry")}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  href={LEGAL_PAGE_URLS[type]}
                  target="_blank"
                  rel="noopener noreferrer"
                  trailingIcon={<ExternalLink size={14} />}
                >
                  {t("legal_open_new_tab")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <footer className="flex flex-wrap justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-slate-700 sm:px-6">
          <Button variant="outline" onClick={requestClose}>
            {t("close")}
          </Button>
          {onAgree && status === "ready" && (
            <Button
              onClick={() => {
                onAgree();
                requestClose();
              }}
            >
              {t("legal_i_agree")}
            </Button>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
}
